import { TimeOption } from '@amzn/aet-galactus-kit';
import arrowLeftLargeTokens from '@amzn/meridian-tokens/base/icon/arrow-left-large';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import Toaster from '@amzn/meridian/toaster';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import Button from '@amzn/meridian/button';
import moment from 'moment';
import queryString from 'qs';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ContactStatus, HMDDetails } from './types';
import {
  HMD_REPEAT_VERTICAL,
  HMD_YES_VERTICAL,
  HMD_NO_VERTICAL,
} from '@amzn/aet-galactus-kit';

import {
  generateContactWithAliasForHmdDetails,
  initialSortForHmdDetails,
  EmptyHmdDetails,
  countHMDResponses,
  EmptyContactStatus,
  filterHMDDetails,
  filterRealtimeHMDDetails,
} from './helper';

import DataSource from '../../../../api';
import { StateContext } from '../../../../GlobalStateStore';
import { EmptyProfile, Profile } from '../../../../Types';
import DataState from '../../../../Types/DataState';
import {
  getCurrentWeekBoundriesList,
  getDateRangeFromWeek,
  getDateRangeFromTimestamp,
  getCurrentWeek,
} from '../../../../Util/Week';
import { renderUrl, urls } from '../../../../config/Url';
import Contacts from './Contacts';
import { TRAILING_DAYS } from '../LighthouseMetrics/types';
import { convertToVerticalOption } from '../../../../Util/Vertical';
import { mergeContactStatusWithHmdDetails } from '../../../../api/Utils/helper';

type DashboardDetailsProps = {
  isHMDRealTime: boolean;
  propProfile?: Profile;
  propProfileState?: string;
};

export const contactTypes = {
  repeat: 'repeat',
  hmdYes: 'hmdYes',
  hmdNo: 'hmdNo',
};

export const EPOCH_START = '1970-01-01T00:00:00.000Z';

// eslint-disable-next-line no-empty-pattern
const DashboardDetails: React.FC<DashboardDetailsProps> = ({
  isHMDRealTime,
  propProfile,
  propProfileState,
}): React.ReactElement => {
  const apiNAWS = DataSource.getDataFeed('NAWS');

  const location = useLocation();
  const routeParams = useParams();
  const queryParams = queryString.parse(location.search.substring(1));
  const metric = location.hash ? location.hash.substring(1) : 'hmdYes';

  const navigate = useNavigate();

  const date = getCurrentWeekBoundriesList();

  const [initialSorting, setInitialSorting] = useState(true);

  // contact listening dates
  const defaultContactDateRange = getDateRangeFromTimestamp();
  const [contactStartDate, setContactStartDate] = useState(
    defaultContactDateRange.contactStartDate
  );
  const [contactEndDate, setContactEndDate] = useState(
    defaultContactDateRange.contactEndDate
  );

  // Filter Options from Params
  const vertical = queryParams.vertical as string;
  const skill = queryParams.skill as string;
  const channel = queryParams.channel as string;
  const segment = queryParams.segment as string;
  const marketplace = queryParams.marketplace as string;
  const timeOption = queryParams.timeOption as TimeOption;
  const timestamp = queryParams.timestamp as string;

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;

  const { cscEntityPrefix, userID } = inspireOptions.data;

  const intl = useIntl();

  const metricsTranslations = {
    hmdYes: intl.formatMessage({
      id: 'cs_perfportal_hmdDetails_hmdYes',
      defaultMessage: 'HMD Yes',
    }),
    hmdNo: intl.formatMessage({
      id: 'cs_perfportal_hmdDetails_hmdNo',
      defaultMessage: 'HMD No',
    }),
    repeat: intl.formatMessage({
      id: 'cs_perfportal_yesRepeat',
      defaultMessage: 'Repeat',
    }),
  };

  const [currentTab, setCurrentTab] = useState(metric);
  const [refreshTrackingValue, setRefreshTrackingValue] = useState('YesTab');
  const [refreshTrackingSlotId, setRefreshTrackingSlotId] = useState(
    'real-time-hmd-refresh-button-realtime-yes'
  );

  const [profile, setProfile] = useState<Profile>(EmptyProfile);
  const [profileState, setProfileState] = useState(DataState.LOADING);

  // Org Vertical
  const [orgVertical, setOrgVertical] = useState<string | undefined>(undefined);
  const [orgVerticalState, setOrgVerticalState] = useState(DataState.LOADING);

  // Hmd Details
  const [hmdDetails, setHmdDetails] = useState<HMDDetails>(EmptyHmdDetails);
  const [hmdDetailsState, setHmdDetailsState] = useState(DataState.LOADING);

  //Contact Status
  const [contactStatusDetails, setContactStatusDetails] =
    useState<ContactStatus>(EmptyContactStatus);
  const [contactStatusDetailsState, setContactStatusDetailsState] = useState(
    DataState.LOADING
  );
  // Toast
  const [showToast, setShowToast] = useState(true);

  const onCloseToast = useCallback(() => {
    setShowToast(false);
  }, []);

  useEffect(() => {
    if (!initialSorting) setInitialSorting(true);
  }, [currentTab, initialSorting]);

  useEffect(() => {
    if (isHMDRealTime) {
      // realtime hmd
      setContactStartDate(date.start);
      setContactEndDate(date.end);
    } else {
      // historical hmd
      let dateRange;
      if (timeOption === TimeOption.DAILY) {
        dateRange = getDateRangeFromWeek(TRAILING_DAYS);
      } else {
        //weekly
        dateRange = getDateRangeFromWeek(timestamp);
      }
      setContactStartDate(dateRange.startDate);
      setContactEndDate(dateRange.endDate);
    }
  }, [date, timestamp, timeOption, isHMDRealTime]);

  const profileId = routeParams?.userId?.split('?')[0] || userID;
  const backURL = renderUrl(`${urls.dashboard}/${profileId}`);

  const filterAndSetHmdDetails = (hmdDetails: HMDDetails) => {
    if (isHMDRealTime) {
      const filteredHmdDetails = filterRealtimeHMDDetails(
        hmdDetails,
        profile.user.owner,
        orgVertical
      );
      setHmdDetails(filteredHmdDetails);
    } else {
      const filteredHmdDetails = filterHMDDetails(
        hmdDetails,
        profile.user.owner
      );
      setHmdDetails(filteredHmdDetails);
    }
  };

  const loadData = async () => {
    if (
      inspireOptions.state === DataState.LOADED &&
      profileState === DataState.LOADED
    ) {
      const routeUserID = routeParams?.userId?.split('?')[0];
      const queryUserID = routeUserID ? routeUserID : userID;
      const isProfileTm = profile.directReports.length > 0;
      if (!isHMDRealTime) {
        const queryVariables = apiNAWS.prepareHmdDetailsQuery(
          queryUserID,
          isProfileTm,
          isProfileTm ? profile.directReports : [],
          vertical,
          skill,
          channel,
          segment,
          marketplace,
          timeOption,
          timestamp,
          cscEntityPrefix
        );
        apiNAWS.getHMDDetails(
          queryVariables,
          filterAndSetHmdDetails,
          setHmdDetailsState
        );
      } else {
        const queryVariables = apiNAWS.prepareRealtimeHmdDetailsQuery(
          queryUserID,
          cscEntityPrefix,
          isProfileTm,
          isProfileTm ? profile.directReports : [],
          date.start,
          date.end
        );

        await apiNAWS.getRealtimeHmdDetails(
          queryVariables,
          filterAndSetHmdDetails,
          setHmdDetailsState
        );
      }
    }
  };

  useEffect(() => {
    loadData();
  }, [inspireOptions.state, profileState]);

  const loadContactStatusData = async () => {
    if (isHMDRealTime && hmdDetailsState === DataState.LOADED) {
      const routeUserID = routeParams?.userId?.split('?')[0];
      const queryUserID = routeUserID ? routeUserID : userID;
      const isProfileTm = profile.directReports.length > 0;

      const contactStatusVariables = apiNAWS.prepareContactStatusQuery(
        queryUserID,
        cscEntityPrefix,
        isProfileTm,
        isProfileTm ? profile.directReports : [],
        moment(date.start).format('GGGG-WW'),
        moment(date.end).format('GGGG-WW')
      );

      await apiNAWS.getContactStatus(
        contactStatusVariables,
        setContactStatusDetails,
        setContactStatusDetailsState
      );
    }
  };

  useEffect(() => {
    loadContactStatusData();
  }, [hmdDetailsState]);

  useEffect(() => {
    if (
      isHMDRealTime &&
      hmdDetailsState === DataState.LOADED &&
      contactStatusDetailsState === DataState.LOADED
    ) {
      const mergedDetails = mergeContactStatusWithHmdDetails(
        hmdDetails,
        contactStatusDetails
      );
      setHmdDetails(mergedDetails);
    }
  }, [isHMDRealTime, hmdDetailsState, contactStatusDetailsState]);

  useEffect(() => {
    if (currentTab === contactTypes.hmdYes) {
      setRefreshTrackingValue('YesTab');
      setRefreshTrackingSlotId('real-time-hmd-refresh-button-realtime-yes');
    } else if (currentTab === contactTypes.hmdNo) {
      setRefreshTrackingValue('NoTab');
      setRefreshTrackingSlotId('real-time-hmd-refresh-button-realtime-no');
    } else if (currentTab === contactTypes.repeat) {
      setRefreshTrackingValue('RepeatTab');
      setRefreshTrackingSlotId('real-time-hmd-refresh-button-realtime-repeat');
    }
  }, [currentTab]);

  // Get profile data for user
  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED) {
      const routeUserID = routeParams?.userId?.split('?')[0];
      const queryUserID = routeUserID ? routeUserID : userID;
      const queryMigrationVariables = apiNAWS.prepareQuery(
        getCurrentWeek(),
        queryUserID,
        cscEntityPrefix
      );
      apiNAWS.getProfileData(
        queryMigrationVariables,
        setProfile,
        setProfileState
      );
    }
  }, [inspireOptions.state]);

  // Get org vertical for user
  useEffect(() => {
    if (isHMDRealTime && profileState === DataState.LOADED) {
      const routeUserID = routeParams?.userId?.split('?')[0];
      const queryUserID = routeUserID ? routeUserID : userID;
      const userIdWithPrefix = `${cscEntityPrefix}:${queryUserID}`;
      const orgVerticalVariables = apiNAWS.prepareOrgVerticalQuery(
        [userIdWithPrefix],
        userIdWithPrefix
      );
      apiNAWS.getOrgVertical(
        orgVerticalVariables,
        setOrgVertical,
        setOrgVerticalState
      );
    }
  }, [profileState, isHMDRealTime, cscEntityPrefix]);

  if (
    profileState === DataState.LOADING ||
    hmdDetailsState === DataState.LOADING ||
    (isHMDRealTime && orgVerticalState === DataState.LOADING)
  ) {
    return (
      <Column width='100%' heights='fill' alignmentHorizontal='center'>
        <Loader type='circular' size='large' />
      </Column>
    );
  }

  if (profileState === DataState.ERROR || hmdDetailsState === DataState.ERROR) {
    return (
      <Alert type='error'>
        <FormattedMessage
          id='cs_perfportal_error_reason_1'
          defaultMessage='Your credentials may be incorrect or inadequate.'
        />
      </Alert>
    );
  }

  const aliasContacts = generateContactWithAliasForHmdDetails(
    hmdDetails.yesResponses,
    hmdDetails.noResponses,
    hmdDetails.repeatResponses,
    profile.user,
    profile.directReports.length > 0 ? profile.directReports : []
  );

  const HMDResponses = countHMDResponses(
    hmdDetails.yesResponses,
    hmdDetails.noResponses,
    hmdDetails.repeatResponses
  );

  const {
    yesResponsesWithAlias,
    noResponsesWithAlias,
    repeatResponsesWithAlias,
  } = aliasContacts;

  const sortedContacts = initialSortForHmdDetails(
    yesResponsesWithAlias,
    noResponsesWithAlias,
    repeatResponsesWithAlias
  );

  const { sortedYesResponses, sortedNoResponses, sortedRepeatResponses } =
    sortedContacts;

  return (
    <Box>
      {showToast && !isHMDRealTime && (
        <>
          <Toaster
            toasts={[
              {
                id: 'cs_perfportal_hmdDetails_schedule_message',
              },
            ]}
            onCloseToast={onCloseToast}
            alignmentHorizontal='center'
          >
            {(): React.ReactElement => (
              <>
                <Alert type='neutral' toast={true} onClose={onCloseToast}>
                  <Text alignment='center'>
                    <FormattedMessage
                      id='cs_perfportal_hmdDetails_schedule_message'
                      defaultMessage='The number of HMD responses may not match your dashboard due to different data refresh schedules.'
                    />
                  </Text>
                </Alert>
              </>
            )}
          </Toaster>
        </>
      )}
      <Row>
        <Icon tokens={arrowLeftLargeTokens} />
        <Link
          href='/'
          onClick={() => navigate(renderUrl(backURL))}
          type='secondary'
        >
          <FormattedMessage
            id='cs_perfportal_navigate_back'
            defaultMessage='Back'
            description='Link to go back'
          />
        </Link>
      </Row>
      <Row>
        <Box spacingInset='400 none 400 none'>
          <Text type='h400'>
            {isHMDRealTime ? (
              <FormattedMessage
                id='cs_perfportal_realTimeHmdDetails_title'
                defaultMessage='Real Time HMD Details'
              />
            ) : (
              <FormattedMessage
                id='cs_perfportal_HmdDetails_title'
                defaultMessage='HMD details'
                description='Title'
              />
            )}
          </Text>
        </Box>
      </Row>
      <Row>
        <TabGroup value={currentTab} onChange={setCurrentTab}>
          {isHMDRealTime ? (
            <>
              <Tab
                value={contactTypes.hmdYes}
                data-csa-c-type='tab'
                data-csa-c-slot-id='open-real-time-hmd-yes-tab'
                data-csa-c-content-id='open-real-time-hmd-yes-tab'
                data-csa-c-description='Real Time HMD Yes Contacts Tab'
                data-csa-c-tag1='Dashboard'
                data-csa-c-tag2='RealTimeHMD'
                data-csa-c-profile-id={`${
                  routeParams?.userId?.split('?')[0]
                    ? routeParams?.userId.split('?')[0]
                    : userID
                }`}
              >
                {metricsTranslations.hmdYes} ({HMDResponses.countYesResponses})
              </Tab>
              <Tab
                value={contactTypes.hmdNo}
                data-csa-c-type='tab'
                data-csa-c-slot-id='open-real-time-hmd-no-tab'
                data-csa-c-content-id='open-real-time-hmd-no-tab'
                data-csa-c-description='Real Time HMD No Contacts Tab'
                data-csa-c-tag1='Dashboard'
                data-csa-c-tag2='RealTimeHMD'
                data-csa-c-profile-id={`${
                  routeParams?.userId?.split('?')[0]
                    ? routeParams?.userId.split('?')[0]
                    : userID
                }`}
              >
                {metricsTranslations.hmdNo} ({HMDResponses.countNoResponses})
              </Tab>
              <Tab
                value={contactTypes.repeat}
                data-csa-c-type='tab'
                data-csa-c-slot-id='open-real-time-hmd-repeat-tab'
                data-csa-c-content-id='open-real-time-hmd-repeat-tab'
                data-csa-c-description='Real Time HMD Repeat Contacts Tab'
                data-csa-c-tag1='Dashboard'
                data-csa-c-tag2='RealTimeHMD'
                data-csa-c-profile-id={`${
                  routeParams?.userId?.split('?')[0]
                    ? routeParams?.userId.split('?')[0]
                    : userID
                }`}
              >
                {metricsTranslations.repeat} (
                {HMDResponses.countRepeatResponses})
              </Tab>
            </>
          ) : (
            <>
              {HMD_YES_VERTICAL.includes(convertToVerticalOption(vertical)) && (
                <Tab value={contactTypes.hmdYes}>
                  {metricsTranslations.hmdYes}
                </Tab>
              )}
              {HMD_NO_VERTICAL.includes(convertToVerticalOption(vertical)) && (
                <Tab value={contactTypes.hmdNo}>
                  {metricsTranslations.hmdNo}
                </Tab>
              )}
              {HMD_REPEAT_VERTICAL.includes(
                convertToVerticalOption(vertical)
              ) && (
                <Tab value={contactTypes.repeat}>
                  {metricsTranslations.repeat}
                </Tab>
              )}
            </>
          )}
        </TabGroup>
      </Row>
      {isHMDRealTime && (
        <Box spacingInset='300 none none none'>
          <Box spacingInset='none none 300 none'>
            <Text type='b300'>
              <FormattedMessage
                id='cs_perfportal_realTimeHmdDetails_reload_part1'
                defaultMessage='{startDate} - {endDate}'
                values={{
                  startDate: moment(date.start).format('MMM D, YYYY'),
                  endDate: moment(date.end).format('MMM D, YYYY'),
                }}
              />
            </Text>
          </Box>
          <Button
            onClick={loadData}
            size='small'
            data-csa-c-type='button'
            data-csa-c-slot-id={refreshTrackingSlotId}
            data-csa-c-tag1='Dashboard'
            data-csa-c-tag2='RealTimeHMD'
            data-csa-c-tag3={refreshTrackingValue}
          >
            <Icon tokens={refreshTokens}></Icon>
            <Box spacingInset='none 300 none none'>
              <Text color='inverted'>
                <FormattedMessage
                  id='cs_perfportal_realTimeHmdDetails_refresh'
                  defaultMessage='Refresh'
                  description='Refresh Button'
                />
              </Text>
            </Box>
          </Button>
        </Box>
      )}
      {hmdDetails.lastAuditDate !== EPOCH_START && (
        <Row>
          <Text>
            <FormattedMessage
              id='cs_perfportal_lastUpdated'
              defaultMessage='Last updated'
            />{' '}
            {moment(hmdDetails.lastAuditDate).utc().format('YYYY-MM-DD HH:mm')}{' '}
            UTC
          </Text>
        </Row>
      )}
      <Row>
        {currentTab === contactTypes.hmdYes && (
          <Contacts
            contacts={sortedYesResponses}
            isHMDRealTime={isHMDRealTime}
            hmdTab={contactTypes.hmdYes}
            contactStartDate={contactStartDate}
            contactEndDate={contactEndDate}
            userId={
              routeParams?.userId?.split('?')[0]
                ? routeParams?.userId.split('?')[0]
                : userID
            }
          />
        )}
        {currentTab === contactTypes.hmdNo && (
          <Contacts
            contacts={sortedNoResponses}
            isHMDRealTime={isHMDRealTime}
            hmdTab={contactTypes.hmdNo}
            contactStartDate={contactStartDate}
            contactEndDate={contactEndDate}
            userId={
              routeParams?.userId?.split('?')[0]
                ? routeParams?.userId.split('?')[0]
                : userID
            }
          />
        )}
        {currentTab === contactTypes.repeat && (
          <Contacts
            contacts={sortedRepeatResponses}
            isHMDRealTime={isHMDRealTime}
            hmdTab={contactTypes.repeat}
            contactStartDate={contactStartDate}
            contactEndDate={contactEndDate}
            userId={
              routeParams?.userId?.split('?')[0]
                ? routeParams?.userId.split('?')[0]
                : userID
            }
          />
        )}
      </Row>
    </Box>
  );
};

export default DashboardDetails;
